/**
 * `AssistantContext` (OneFleet AD-04): a module may scope the shared assistant panel to its own endpoint and
 * context — the `/fleet` module mounts it with OneFleet's AI API (`/fleet-ai/v1/chat`, the same envelope shape
 * plus `evidence[]`, `tables[]`, `warnings[]`), its headers (`X-Company`) and per-page quick prompts. Outside such
 * a provider the panel keeps OneSphere's own assistant; nothing else in the shell changes.
 */
import { createContext, useContext, type ReactNode } from "react";

export type AssistantScope = {
  /** POST endpoint answering `{messages, language, context}` with the assistant envelope. */
  endpoint: string;
  /** Extra headers per request (tenant, correlation). The bearer token is always added by the panel. */
  headers?: () => Record<string, string>;
  /** Context sent with each question (page, selection, company). */
  context?: () => Record<string, unknown>;
  /** Quick prompts shown before the first question (already translated). */
  quickPrompts?: string[];
  /** Panel title and subtitle (already translated). */
  title?: string;
  subtitle?: string;
  /** Prefix for deep links in the evidence (the module's base path). */
  linkBase?: string;
};

const Ctx = createContext<AssistantScope | null>(null);

export function AssistantContextProvider({ value, children }: { value: AssistantScope | null; children: ReactNode }) {
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAssistantScope(): AssistantScope | null {
  return useContext(Ctx);
}
